import { GripVertical, Settings, Trash2 } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FIELD_TYPES } from './FieldTypePicker'
import type { ApplicationField } from '@/types'

interface Props {
  field: ApplicationField
  onEdit: () => void
  onDelete: () => void
}

export function FieldBlock({ field, onEdit, onDelete }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const typeInfo = FIELD_TYPES.find((t) => t.type === field.type)
  const Icon = typeInfo?.icon

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border bg-background px-3 py-2.5 shadow-sm"
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Type icon */}
      {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}

      {/* Label */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {field.label || (field.type === 'notice' ? (field.description?.split('\n')[0]?.slice(0, 60) || 'Notice') : 'Untitled field')}
        </p>
        <p className="text-xs text-muted-foreground">{typeInfo?.label}</p>
      </div>

      {field.required && (
        <Badge variant="secondary" className="shrink-0 text-xs">
          Required
        </Badge>
      )}

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
          <Settings className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
